import { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { supabase } from "@/supabase/client";
import { Item, NewItem, MemberSetting } from "@/types/purchase";
import { logUsage } from '@/lib/usageLog';
import { format } from "date-fns";
import * as orderService from "@/supabase/order/service/orderService";
import * as orderItemRepository from "@/supabase/order/repository/orderItemRepository";
import { logError } from "@/lib/sentry";

// 순수 헬퍼: 훅 내부에 두면 매 렌더마다 새 함수가 생기므로 모듈 스코프로 호이스트.
// (Stage 2: 5개 핸들러 reference 안정화의 일환)
const extractOrderIdFromCustomerId = (customerId: string): number | null => {
  if (customerId && customerId.startsWith('order_')) {
    return Number(customerId.replace('order_', ''));
  }
  return null;
};

const isTempCustomerIdValue = (customerId: string | null | undefined): boolean => {
  return !!customerId && customerId.startsWith('temp_');
};

// 초기 상태 상수 정의
export const INITIAL_STATE = {
  memberSetting: {
    shippingFee: 3500,
    freeShippingThreshold: 70000,
    depositAccount: "",
    messageTemplate: `[고객 닉네임]

구매해주셔서 감사합니다❤️

[상품목록]

📦 상품 금액: [금액]
🚚 배송비: [배송비]
💰 입금액: [총금액]
💳 입금계좌: [입금계좌]

입금 시 닉네임으로 입금 부탁드립니다.

✅ 닉네임:
✅ 입금자명:
✅ 연락처:
✅ 주소:
✅ 배송메모:`,
    settingName: "기본 설정",
    itemEmoji: "📍",
    jejuShippingFee: 6000,
    jejuFreeShippingFee: 0,
    shippingFeePresets: []
  },
  shippingFee: 3500,
  freeShippingThreshold: 70000,
  // recentItems: [] // Removed
};

export function usePurchaseState(
  user: any,
  pseudoUserId: string,
  items: Item[],
  onItemsChange: (items: Item[]) => void,
  addOrUpdateItemInCatalog: (item: Item) => void, // productCatalog 업데이트 함수를 prop으로 받음
  activeCustomerId?: string | null,
  customerNickname?: string | null,
  selectedDate?: Date,
  onItemsChanged?: () => void, // 상품 목록이 변경되었을 때 호출할 콜백 함수
  customerShippingFeeOverride?: number | null, // Added prop
  onSetShippingFeeOverride?: (value: number | null) => void, // Added callback prop
  onOrderMutated?: (orderId: number) => void // 기존 주문 가격 뮤테이션 성공 후 status reconcile (스펙 §16.10)
) {
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);

  // 설정 관련 상태
  const [shippingFee, setShippingFee] = useState(3500);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(70000);
  const [template, setTemplate] = useState(INITIAL_STATE.memberSetting.messageTemplate);
  
  // 사용자 설정 관련 상태
  const [memberSetting, setMemberSetting] = useState<MemberSetting>(INITIAL_STATE.memberSetting);
  const [memberSettings, setMemberSettings] = useState<MemberSetting[]>([]);
  // const [shippingFeeOverride, setShippingFeeOverride] = useState<number | null>(null); // Removed internal state

  // 로그아웃 감지 및 상태 초기화
  // 주의: deps를 []로 유지하여 onAuthStateChange 구독이 한 번만 생성되도록 함.
  // Supabase JS v2는 구독 시 INITIAL_SESSION 이벤트를 발생시키므로,
  // 구독이 재생성되면 AuthContext에서 setUser가 호출되어 전체 주문 목록이 리로드됨.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // 모든 상태 초기화
        setMemberSetting(INITIAL_STATE.memberSetting);
        setShippingFee(INITIAL_STATE.shippingFee);
        setFreeShippingThreshold(INITIAL_STATE.freeShippingThreshold);
        setTemplate(INITIAL_STATE.memberSetting.messageTemplate);
        // items 클리어 제거: A2에서 currentItems는 activeCustomer.items에서 derive되고,
        // 로그아웃 시 세션 스토어 기준으로 전체 리로드되어 자동으로 비워짐.
        // (이전엔 onItemsChange([])로 비웠으나, 이제 stale id로 source를 비울 위험이 있어 제거.)
        setMemberSettings([]);
        setCurrentOrderId(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Removed useEffect that reset shippingFeeOverride on customer change

  // 사용자 설정 불러오기
  useEffect(() => {
    const loadUserSettings = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          // 로그인하지 않은 경우 초기 상태로 설정
          setMemberSetting(INITIAL_STATE.memberSetting);
          setShippingFee(INITIAL_STATE.shippingFee);
          setFreeShippingThreshold(INITIAL_STATE.freeShippingThreshold);
          setTemplate(INITIAL_STATE.memberSetting.messageTemplate);
          setMemberSettings([]);
          return;
        }

        const { data, error } = await supabase
          .from('member_setting')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (data && data.length > 0) {
          const settings = data.map(item => ({
            id: item.id,
            shippingFee: item.shipping_fee,
            freeShippingThreshold: item.free_shipping_threshold,
            depositAccount: item.deposit_account,
            messageTemplate: item.message_template,
            settingName: item.setting_name,
            itemEmoji: item.item_emoji || '📍',
            jejuShippingFee: item.jeju_shipping_fee,
            jejuFreeShippingFee: item.jeju_free_shipping_fee ?? 0,
            shippingFeePresets: Array.isArray(item.shipping_fee_presets) ? item.shipping_fee_presets : [],
          }));
          
          setMemberSettings(settings);
          
          // 첫 번째 설정을 현재 설정으로 로드
          const currentSetting = settings[0];
          setMemberSetting(currentSetting);
          setShippingFee(currentSetting.shippingFee);
          setFreeShippingThreshold(currentSetting.freeShippingThreshold);
        }
      } catch (error) {
        logError('설정을 불러오는데 실패했습니다:', error);
      }
    };

    loadUserSettings();
  }, []);

  // 최근 상품 목록 관련 로직은 useProductCatalog로 이동되었으므로 삭제
  // // 최근 상품 목록 세션 스토리지 저장 및 불러오기
  // useEffect(() => {
  //   const userId = user?.id || pseudoUserId;
  //   const savedRecentItems = sessionStorage.getItem(`recentItems_${userId}`);
  //   if (savedRecentItems && recentItems.length === 0) {
  //     try {
  //       setRecentItems(JSON.parse(savedRecentItems));
  //     } catch (e) {
  //       logError("저장된 최근 상품 목록을 불러오는데 실패했습니다:", e);
  //     }
  //   }
  // }, [user, pseudoUserId]);
  
  // // 최근 상품 목록이 변경될 때마다 세션 스토리지에 저장
  // useEffect(() => {
  //   // 컴포넌트가 마운트된 후 recentItems가 실제로 변경되었을 때만 저장
  //   if (recentItems.length > 0) {
  //     const userId = user?.id || pseudoUserId;
  //     sessionStorage.setItem(`recentItems_${userId}`, JSON.stringify(recentItems));
  //   }
  // }, [recentItems, user, pseudoUserId]);

  // 주문 관련 헬퍼 함수들
  // extractOrderId / isTempCustomerId 는 모듈 스코프(extractOrderIdFromCustomerId,
  // isTempCustomerIdValue)로 호이스트됨. 5개 핸들러 reference 안정화 작업의 일부.

  // Stage 2: latest-ref 패턴.
  // 5개 핸들러(addItem / removeItemByIndex / updateItemQuantity / updateItem / addRecentItem)
  // 가 캡처해야 하는 closure 값들(props·state)을 매 렌더 ref에 동기화한 뒤,
  // 핸들러는 deps=[]로 묶어 reference identity를 영구 안정화한다.
  //
  // 왜 ref 패턴인가:
  // - 핸들러들은 `items`(외부 controlled state), `onItemsChange`(callback prop),
  //   `customerNickname`/`activeCustomerId`/`selectedDate`/`user` 등 다수의 외부 값을 읽는다.
  // - 이 값들을 useCallback deps에 넣으면 매 렌더 핸들러가 새로 생성 → memo bail-out 깨짐.
  // - 함수형 setState로 환원 불가(외부 prop callback이지 local setState가 아님).
  // - Ref에 commit 후 동기화 → 핸들러는 항상 *커밋된* 값을 본다 → stale closure 차단.
  //
  // 왜 useLayoutEffect인가 (Codex Stage 2 review 반영):
  // - 렌더 본체에서 `latestRef.current = {...}` 동기 갱신은 React 18 concurrent rendering
  //   에서 안전하지 않다: 어떤 props로 렌더가 시작돼 ref를 덮어쓴 뒤 그 렌더가
  //   *버려지면*, 커밋된 UI는 옛 상태인데 ref는 버려진 렌더의 값을 들고 있어
  //   handler가 잘못된 주문/고객에 작업을 가할 수 있다.
  // - useLayoutEffect는 *commit 직후* 그리고 *브라우저 paint·event 처리 전*에 동기 실행되어,
  //   커밋된 props/state만 ref에 반영한다. 버려진 렌더의 값은 새지 않는다.
  //   handler는 event 처리 시점에 호출되므로 항상 *커밋된* 최신 값을 본다.
  const latestRef = useRef({
    items,
    user,
    customerNickname,
    activeCustomerId,
    selectedDate,
    onItemsChange,
    onItemsChanged,
    addOrUpdateItemInCatalog,
    onOrderMutated,
  });
  useLayoutEffect(() => {
    latestRef.current = {
      items,
      user,
      customerNickname,
      activeCustomerId,
      selectedDate,
      onItemsChange,
      onItemsChanged,
      addOrUpdateItemInCatalog,
      onOrderMutated,
    };
  });

  // 상품 관련 함수들
  const resetOrder = useCallback(() => {
    // 비회원 사용자는 UI만 초기화
    if (!user) {
      onItemsChange([]);
      return;
    }

    // 로그인 사용자는 DB에서 모든 상품을 삭제
    const orderId = activeCustomerId
      ? (activeCustomerId.startsWith('order_')
        ? Number(activeCustomerId.replace('order_', ''))
        : null)
      : currentOrderId;

    if (orderId) {
      orderService.removeAllItemsFromOrder(orderId)
        .then(() => {
          onItemsChange([]); // UI 업데이트
          if (onItemsChanged) onItemsChanged();
          latestRef.current.onOrderMutated?.(orderId);   // total_price 0 반영 후 status reconcile (§16.10)
        })
        .catch(error => {
          logError("주문 초기화 중 오류 발생:", error);
          alert("상품 목록 초기화에 실패했습니다.");
        });
    } else {
      // orderId가 없는 경우 (DB에 아직 저장되지 않은 신규 고객 등)
      onItemsChange([]);
    }
  }, [user, activeCustomerId, currentOrderId, onItemsChange, onItemsChanged]);

  // Stage 2: useCallback(deps=[]) + latestRef.
  // latestRef를 통해 매 호출 시 *최신* items/user/... 를 읽으므로 stale closure 차단.
  const addItem = useCallback(async (payload: NewItem) => {
    if (!payload.name.trim()) {
      alert("상품명을 입력해주세요.");
      return;
    }

    // 가격 입력이 비어있으면(placeholder만 보이는 상태 등) 0원으로 간주
    const rawPrice = payload.price.trim();
    const price = rawPrice === "" ? 0 : Number(rawPrice);
    if (isNaN(price)) {
      alert("올바른 가격을 입력해주세요.");
      return;
    }

    if (price < 0) {
      if (!confirm("상품 가격이 음수입니다. 정말 추가하시겠습니까?")) {
        return;
      }
    }

    const quantity = Number(payload.quantity);
    if (isNaN(quantity) || quantity <= 0) {
      alert("수량은 1 이상이어야 합니다.");
      return;
    }

    const newItemData = {
      name: payload.name.trim(),
      price: price,
      quantity: quantity
    };

    // latestRef에서 *최신* 클로저 값 스냅샷.
    // 핸들러 본체 진입 시점 기준으로 한 번만 읽어 동일 호출 내 일관성 보장.
    const {
      items,
      user,
      customerNickname,
      activeCustomerId,
      selectedDate,
      onItemsChange,
      onItemsChanged,
      addOrUpdateItemInCatalog,
      onOrderMutated,
    } = latestRef.current;

    // 같은 이름과 가격의 상품이 있는지 확인
    const existingItemIndex = items.findIndex(
      item => item.name === newItemData.name && item.price === newItemData.price
    );

    // 비회원 사용자는 기존대로 처리
    if (!user) {
      let updatedItems;
      if (existingItemIndex !== -1) {
        // 이미 존재하는 상품이면 수량만 증가
        updatedItems = [...items];
        updatedItems[existingItemIndex].quantity += newItemData.quantity;
      } else {
        // 새로운 상품이면 추가
        updatedItems = [...items, newItemData];
      }

      // 상품 목록 변경을 부모에게 알림
      onItemsChange(updatedItems);
    }
    // 로그인 사용자는 DB에 저장
    else {
      try {
        if (!customerNickname) {
          alert("고객 닉네임을 선택해주세요.");
          return;
        }

        // 현재 선택된 주문 ID 확인 (activeCustomerId에서 추출)
        const orderId = activeCustomerId ? extractOrderIdFromCustomerId(activeCustomerId) : null;
        const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");

        // UI 최적화를 위해 먼저 로컬 상태 업데이트
        let updatedItems;
        if (existingItemIndex !== -1) {
          // 이미 존재하는 상품이면 수량만 증가
          updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += newItemData.quantity;
        } else {
          // 새로운 상품이면 추가
          updatedItems = [...items, newItemData];
        }

        // 상품 목록 변경을 부모에게 먼저 알림 (UI는 바로 업데이트됨)
        onItemsChange(updatedItems);

        // 이후 백그라운드에서 DB 업데이트 수행
        if (items.length === 0) {
          // 첫번째 상품 추가: createWithItem 호출
          const newOrderId = await orderService.createWithItem(
            user.id,
            customerNickname,
            formattedDate,
            {
              itemName: newItemData.name,
              price: newItemData.price,
              quantity: newItemData.quantity
            }
          );

          setCurrentOrderId(newOrderId);
        } else if (existingItemIndex !== -1 && orderId) {
          // 기존 상품 수량 증가: updateItemQuantity 호출
          const existingItem = items[existingItemIndex];
          const updatedQuantity = existingItem.quantity + newItemData.quantity;

          // 해당 상품의 OrderItem ID를 찾아야 함
          const orderItems = await orderItemRepository.findByOrderId(orderId);
          const orderItem = orderItems.find(item =>
            item.item_name === existingItem.name && item.price === existingItem.price
          );

          if (orderItem) {
            await orderService.updateItem(orderId, orderItem.id, { quantity: updatedQuantity });
            onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
          }
        } else if (orderId) {
          // 새 상품 추가: addItem 호출
          await orderService.addItem(orderId, {
            itemName: newItemData.name,
            price: newItemData.price,
            quantity: newItemData.quantity
          });
          onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
        }

        if (onItemsChanged) onItemsChanged();
      } catch (error) {
        logError("상품 추가 중 오류 발생:", error);
        alert("상품 추가 중 오류가 발생했습니다.");
      }
    }

    // 상품 카탈로그에 아이템 추가/업데이트
    addOrUpdateItemInCatalog(newItemData);

  }, []);

  // Stage 2: useCallback(deps=[]) + latestRef.
  // 상품 삭제 함수
  const removeItemByIndex = useCallback(async (index: number) => {
    const { items, user, activeCustomerId, onItemsChange, onItemsChanged, onOrderMutated } = latestRef.current;

    if (index < 0 || index >= items.length) return;

    // 비회원 사용자는 기존대로 처리
    if (!user) {
      const updatedItems = items.filter((_, i) => i !== index);
      onItemsChange(updatedItems);
      return;
    }

    // 로그인 사용자는 DB 업데이트 + UI 최적화
    try {
      const orderId = activeCustomerId ? extractOrderIdFromCustomerId(activeCustomerId) : null;
      if (!orderId) return;

      // UI 최적화를 위해 UI를 즉시 업데이트
      const updatedItems = items.filter((_, i) => i !== index);
      onItemsChange(updatedItems);

      // 백그라운드에서 DB 업데이트
      const item = items[index];

      // 해당 상품의 orderItem ID를 찾아야 함
      const orderItems = await orderItemRepository.findByOrderId(orderId);
      const orderItem = orderItems.find(oi =>
        oi.item_name === item.name && oi.price === item.price
      );

      if (!orderItem) return;

      // 항상 특정 상품만 삭제하도록 로직 변경 (주문은 남김)
      await orderService.removeItem(orderId, orderItem.id);
      onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)

      if (onItemsChanged) onItemsChanged();
    } catch (error) {
      logError("상품 삭제 중 오류 발생:", error);
      alert("상품 삭제 중 오류가 발생했습니다.");
    }
  }, []);

  // Stage 2: useCallback(deps=[]) + latestRef.
  // 상품 정보 일괄 변경 함수
  const updateItem = useCallback(async (index: number, data: Partial<Omit<Item, 'id'>>) => {
    const { items, user, activeCustomerId, onItemsChange, onItemsChanged, onOrderMutated } = latestRef.current;

    if (index < 0 || index >= items.length) return;

    const originalItem = items[index];
    const updatedItems = [...items];
    updatedItems[index] = { ...originalItem, ...data };

    // 1. Optimistic UI Update
    onItemsChange(updatedItems);

    // 2. Handle non-logged-in users
    if (!user) {
      return;
    }

    // 3. DB Update for logged-in users
    try {
      const orderId = activeCustomerId ? extractOrderIdFromCustomerId(activeCustomerId) : null;
      if (!orderId) {
        throw new Error("주문 정보를 찾을 수 없어 상품 정보를 변경할 수 없습니다.");
      }

      const orderItems = await orderItemRepository.findByOrderId(orderId);
      // DB에서 아이템을 찾을 때는 optimistic update 이전의 원본 데이터 사용
      const orderItemToUpdate = orderItems.find(oi =>
        oi.item_name === originalItem.name && oi.price === originalItem.price
      );

      if (!orderItemToUpdate) {
        throw new Error(`상품 정보(이름: ${originalItem.name}, 가격: ${originalItem.price})를 DB에서 찾을 수 없습니다.`);
      }

      const serviceData: { itemName?: string; price?: number; quantity?: number } = {};
      if (data.name !== undefined) serviceData.itemName = data.name;
      if (data.price !== undefined) serviceData.price = data.price;
      if (data.quantity !== undefined) serviceData.quantity = data.quantity;

      await orderService.updateItem(orderId, orderItemToUpdate.id, serviceData);
      // 가격·수량 변경만 total_price에 닿음 — 이름만 변경은 reconcile 불필요 (§16.10)
      if (data.price !== undefined || data.quantity !== undefined) onOrderMutated?.(orderId);

      if (onItemsChanged) onItemsChanged();
    } catch (error) {
      logError("상품 정보 업데이트 중 오류 발생:", error);
      alert("상품 정보 업데이트 중 오류가 발생했습니다. UI를 이전 상태로 되돌립니다.");
      // 에러 발생 시 UI 롤백 (스냅샷한 items 사용 — latestRef.current.items는 그 사이에
      // 변할 수 있으므로 *호출 진입 시점*의 items로 되돌림)
      onItemsChange(items);
      throw error;
    }
  }, []);

  // Stage 2: useCallback. updateItem이 안정화됐으므로 deps=[updateItem]도 영구 안정.
  // 상품 수량 변경 함수
  const updateItemQuantity = useCallback(async (index: number, newQuantity: number) => {
    const { items } = latestRef.current;
    if (index < 0 || index >= items.length || newQuantity <= 0) return;
    await updateItem(index, { quantity: newQuantity });
  }, [updateItem]);

  // 계산 관련 함수들
  const totalAmount = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  // 총 구매 건수 = 상품 수량 합계 (품목A 3개 + 품목B 4개 = 7건)
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  // 배송비 계산 우선순위:
  // 1. 강제 무료(override=0)
  // 2. 무료배송 기준 달성 시: 제주 모드면 jejuFreeShippingFee, 아니면 0
  // 3. override 지정값(기준 미달)
  // 4. 기본 배송비
  const calculatedShippingFee = (() => {
    if (customerShippingFeeOverride === 0) return 0;
    const isThresholdMet = freeShippingThreshold > 0 && totalAmount >= freeShippingThreshold;
    // 제주 모드 (override > 0)
    if (typeof customerShippingFeeOverride === 'number' && customerShippingFeeOverride > 0) {
      // 프리셋 의미론 분리(PR3): 등록된 '다른 배송비'는 기준 도달해도 지정 금액 유지
      // (memberSetting.shippingFeePresets는 #115 이후 커밋본만 담김 — 드래프트 비유입).
      if (memberSetting.shippingFeePresets.includes(customerShippingFeeOverride)) {
        return customerShippingFeeOverride;
      }
      if (isThresholdMet) {
        return memberSetting.jejuFreeShippingFee > 0 ? memberSetting.jejuFreeShippingFee : 0;
      }
      return customerShippingFeeOverride;
    }
    // 자동 모드
    return isThresholdMet ? 0 : shippingFee;
  })();
  const paymentAmount = totalAmount + calculatedShippingFee;

  // Stage 2: useCallback(deps=[]) + latestRef.
  const addRecentItem = useCallback((item: Item) => {
    // 상품 데이터 유효성 검사
    if (!item || !item.name.trim()) {
      alert("상품명이 없는 항목은 추가할 수 없습니다.");
      return;
    }

    if (isNaN(item.price)) {
      alert("올바른 가격 정보가 없는 항목은 추가할 수 없습니다.");
      return;
    }

    // 수량이 유효하지 않으면 기본값 1로 설정
    if (isNaN(item.quantity) || item.quantity <= 0) {
      item.quantity = 1;
    }

    // latestRef에서 *최신* 클로저 값 스냅샷.
    const {
      items,
      user,
      customerNickname,
      activeCustomerId,
      selectedDate,
      onItemsChange,
      onItemsChanged,
      addOrUpdateItemInCatalog,
      onOrderMutated,
    } = latestRef.current;

    const existingItemIndex = items.findIndex(
      existing => existing.name === item.name && existing.price === item.price
    );

    // 비회원 사용자는 기존대로 처리
    if (!user) {
      let updatedItems;
      if (existingItemIndex !== -1) {
        updatedItems = [...items];
        updatedItems[existingItemIndex].quantity += item.quantity;
      } else {
        updatedItems = [...items, item];
      }

      onItemsChange(updatedItems);
      return;
    }

    // 로그인 사용자는 DB에 저장
    try {
      if (!customerNickname) {
        alert("고객 닉네임을 선택해주세요.");
        return;
      }

      // 임시 고객 ID 체크 - 실제 주문이 없으면 생성해야 함
      if (isTempCustomerIdValue(activeCustomerId)) {
        // 임시 고객 ID인 경우 상위 컴포넌트에서 처리할 수 있도록 함
        // 먼저 최근 상품 추가 후, addItem 함수를 통해 실제 주문이 생성될 것임
        onItemsChange([...items, item]);
        return;
      }

      const orderId = activeCustomerId ? extractOrderIdFromCustomerId(activeCustomerId) : null;
      const formattedDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd");

      // UI 최적화를 위해 UI를 먼저 업데이트
      let updatedItems;
      if (existingItemIndex !== -1) {
        updatedItems = [...items];
        updatedItems[existingItemIndex].quantity += item.quantity;
      } else {
        updatedItems = [...items, item];
      }

      onItemsChange(updatedItems);

      // 백그라운드에서 DB 업데이트.
      // (호출 시점 스냅샷한 items/customerNickname/... 을 그대로 사용 → 비동기 도중 latestRef가
      // 갱신돼도 본 작업은 호출 시점 상태 기준으로 진행. 원본 동작과 동일.)
      (async () => {
        if (items.length === 0) {
          // 첫번째 상품 추가: createWithItem 호출
          const newOrderId = await orderService.createWithItem(
            user.id,
            customerNickname,
            formattedDate,
            {
              itemName: item.name,
              price: item.price,
              quantity: item.quantity
            }
          );

          setCurrentOrderId(newOrderId);
        } else if (existingItemIndex !== -1 && orderId) {
          // 기존 상품 수량 증가: updateItemQuantity 호출
          const existingItem = items[existingItemIndex];
          const updatedQuantity = existingItem.quantity + item.quantity;

          // 해당 상품의 OrderItem ID를 찾아야 함
          const orderItems = await orderItemRepository.findByOrderId(orderId);
          const orderItem = orderItems.find(item =>
            item.item_name === existingItem.name && item.price === existingItem.price
          );

          if (orderItem) {
            await orderService.updateItem(orderId, orderItem.id, { quantity: updatedQuantity });
            onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
          }
        } else if (orderId) {
          // 새 상품 추가: addItem 호출
          await orderService.addItem(orderId, {
            itemName: item.name,
            price: item.price,
            quantity: item.quantity
          });
          onOrderMutated?.(orderId);   // 기존 주문 total_price 변경 커밋 후 status reconcile (§16.10)
        }

        if (onItemsChanged) onItemsChanged();
      })();
    } catch (error) {
      logError("최근 상품 추가 중 오류 발생:", error);
      alert("상품 추가 중 오류가 발생했습니다.");
    }
    // 상품 카탈로그에 아이템 추가/업데이트 (addRecentItem에서도 호출)
    addOrUpdateItemInCatalog(item);
  }, []);

  const generateMessage = (customTemplate?: string) => {
    const itemsList = items
      .map((item) => {
        const priceText = `${item.price.toLocaleString()}`;
        const quantityText = item.quantity > 1 ? ` x ${item.quantity}개` : '';
        return `${memberSetting.itemEmoji} ${item.name} ${priceText}${quantityText}`;
      })
      .join("\n") + "\n";
    
    // 커스텀 템플릿이 있으면 사용하고, 없으면 기본 템플릿 사용
    const templateToUse = customTemplate || template;
    
    let message = templateToUse
      .replace(/\[상품목록\]/g, itemsList.trim())
      .replace(/\[금액\]/g, `${totalAmount.toLocaleString()}원`)
      .replace(/\[배송비\]/g, calculatedShippingFee === 0 ? "0원" : `${calculatedShippingFee.toLocaleString()}원`)
      .replace(/\[총금액\]/g, `${paymentAmount.toLocaleString()}원`)
      .replace(/\[총건수\]/g, `${totalQuantity}건`)
      .replace(/\[입금계좌\]/g, memberSetting.depositAccount || "");

    // 고객 닉네임 변수 치환
    // customerNickname은 usePurchaseState 훅의 파라미터로 전달받음
    message = message.replace(/\[고객 닉네임\]/g, customerNickname ? `${customerNickname}님` : "");
      
    return message;
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generateMessage());
    
    // 사용 로그 기록
    await logUsage({
      userId: user?.id,
      pseudoUserId,
      isMember: !!user,
      type: 'clipboard'
    });
  };

  return {
    // 상태
    // productCatalog 관련 상태는 Home.tsx에서 관리
    shippingFee, setShippingFee,
    freeShippingThreshold, setFreeShippingThreshold,
    template, setTemplate,
    memberSetting, setMemberSetting,
    memberSettings, setMemberSettings,
    currentOrderId, setCurrentOrderId,
    // shippingFeeOverride, // Removed from return, will be handled by Home.tsx
    setShippingFeeOverride: onSetShippingFeeOverride, // Pass through the callback
    
    // 계산된 값
    totalAmount,
    calculatedShippingFee,
    paymentAmount,
    
    // 함수
    resetOrder,
    addItem,
    addRecentItem,
    removeItemByIndex,
    updateItemQuantity,
    updateItem,
    generateMessage,
    copyToClipboard
  };
}